import { humanizeMarketplaceId } from "../../lib/marketplace"
import { useValueRecoverySettings } from "../../services/use-value-recovery-settings"
import { Button } from "../ui/button"
import { ValueRecoveryControl } from "./value-recovery-control"

export function ValueRecoveryRow({
  marketplace,
  canEdit,
}: {
  marketplace: string
  canEdit: boolean
}) {
  const settings = useValueRecoverySettings(marketplace, canEdit)
  const { status, isSaving } = settings
  const marketplaceName = humanizeMarketplaceId(marketplace)
  const isChecking = status.isFetching || isSaving
  const controls = status.isSuccess ? status.data.controls : undefined
  return (
    <li className="space-y-4 px-5 py-5" aria-labelledby={`recovery-${marketplace}`}>
      <div className="flex items-center justify-between gap-3">
        <h3 id={`recovery-${marketplace}`} className="min-w-0 break-words text-sm font-semibold">
          {marketplaceName}
        </h3>

        <Button
          aria-label={`Refresh settings for ${marketplaceName}`}
          disabled={isChecking}
          onClick={settings.refresh}
        >
          Refresh
        </Button>
      </div>

      {controls ? (
        <div className="space-y-5">
          <ValueRecoveryControl
            id={`replacement-${marketplace}`}
            title="Recover values automatically"
            description="Build and activate safe replacements. Changes needing review stay blocked."
            marketplaceName={marketplaceName}
            enabled={controls.replacementEnabled}
            disabled={!settings.canChange}
            isChecking={isChecking}
            onChange={(enabled) => settings.change({ field: "replacementEnabled", enabled })}
          />

          <ValueRecoveryControl
            id={`validation-${marketplace}`}
            title="Recheck runs automatically"
            description="Run fresh checks once product values are current."
            marketplaceName={marketplaceName}
            enabled={controls.validationEnabled}
            disabled={!settings.canChange}
            isChecking={isChecking}
            onChange={(enabled) => settings.change({ field: "validationEnabled", enabled })}
          />
        </div>
      ) : null}

      <ValueRecoveryFeedback settings={settings} />
    </li>
  )
}

function ValueRecoveryFeedback({
  settings,
}: {
  settings: ReturnType<typeof useValueRecoverySettings>
}) {
  const { status, save, isSaving } = settings
  const isChecking = status.isFetching || isSaving
  return (
    <>
      {status.isPending ? (
        <p role="status" className="text-sm text-muted-foreground">
          Loading saved settings…
        </p>
      ) : null}

      {status.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Saved settings are unknown. Refresh before making another change.
        </p>
      ) : null}

      {isSaving ? (
        <p role="status" className="text-sm text-muted-foreground">
          Saving and confirming…
        </p>
      ) : null}

      {!isSaving && status.isFetching && !status.isPending ? (
        <p role="status" className="text-sm text-muted-foreground">
          Refreshing saved settings…
        </p>
      ) : null}

      {!isChecking && status.isSuccess && save.isSuccess ? (
        <p role="status" className="text-sm text-muted-foreground">
          Saved change confirmed.
        </p>
      ) : null}

      {!isChecking && status.isSuccess && save.error ? (
        <p role="alert" className="text-sm text-destructive">
          {save.error.message}
        </p>
      ) : null}
    </>
  )
}
